/**
 * SDL UI Manager
 * Handles screen navigation, modals, and UI state
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import './styles/main.css';

export type ScreenId = 'home' | 'lobby' | 'dice' | 'crew' | 'shop' | 'settings' | 'notifications';

export interface ModalConfig {
  icon: string;
  title: string;
}

export interface UIManagerOptions {
  container: HTMLElement;
  initialScreen?: ScreenId;
}

interface NavItem {
  id: ScreenId;
  icon: string;
  label: string;
  hasBadge?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'home', icon: '/assets/icons/nav/home.svg', label: 'Home' },
  { id: 'dice', icon: '/assets/icons/nav/dice.svg', label: 'Dice' },
  { id: 'crew', icon: '/assets/icons/nav/crew.svg', label: 'Crew', hasBadge: true },
  { id: 'shop', icon: '/assets/icons/nav/shop.svg', label: 'Shop' },
  { id: 'settings', icon: '/assets/icons/nav/settings.svg', label: 'Settings' },
];

/**
 * Screens reachable via bottom-nav swipe gestures. Sub-screens like 'lobby'
 * (opened from a button, not the nav bar) are intentionally excluded so swipes
 * on the home screen do not accidentally land users on them.
 */
const SWIPEABLE_SCREENS: ScreenId[] = NAV_ITEMS.map((i) => i.id);

const MODAL_CONFIG: Partial<Record<ScreenId, ModalConfig>> = {
  home: { icon: '🏠', title: 'HOME' },
  lobby: { icon: '🎯', title: 'STREET LOBBY' },
  dice: { icon: '🎲', title: 'DICE COLLECTION' },
  crew: { icon: '👥', title: 'MY CREW' },
  shop: { icon: '🛒', title: 'SHOP' },
  settings: { icon: '⚙️', title: 'SETTINGS' },
  notifications: { icon: '🔔', title: 'NOTIFICATIONS' },
};

const SCREENS_ORDER: ScreenId[] = ['home', 'lobby', 'dice', 'crew', 'shop', 'settings'];

/**
 * Sub-screens that should hide the bottom nav while active.
 * The nav slides down out of view in lockstep with the incoming screen.
 */
const HIDES_NAV: ReadonlySet<ScreenId> = new Set(['lobby']);

export class UIManager extends EventEmitter {
  private container: HTMLElement;
  private root: HTMLElement;
  private screenContainer: HTMLElement;
  private nav: HTMLElement;
  private modalOverlay: HTMLElement;
  private modalContainer: HTMLElement;
  private modalContent: HTMLElement;
  private modalIcon: HTMLElement;
  private modalTitle: HTMLElement;

  private screens: Map<ScreenId, HTMLElement> = new Map();
  private currentScreen: ScreenId;
  private isAnimating = false;
  private isModalOpen = false;

  constructor(options: UIManagerOptions) {
    super();
    this.container = options.container;
    this.currentScreen = options.initialScreen || 'home';

    this.root = this.createRoot();
    this.screenContainer = this.createScreenContainer();
    this.nav = this.createNavigation();

    const modal = this.createModal();
    this.modalOverlay = modal.overlay;
    this.modalContainer = modal.container;
    this.modalContent = modal.content;
    this.modalIcon = modal.icon;
    this.modalTitle = modal.title;

    this.setupTouchSwipe();
  }

  private createRoot(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'sdl-ui';

    // Background layers
    const bgGradient = document.createElement('div');
    bgGradient.className = 'sdl-bg sdl-bg-gradient';
    root.appendChild(bgGradient);

    const bgNoise = document.createElement('div');
    bgNoise.className = 'sdl-bg sdl-bg-noise';
    root.appendChild(bgNoise);

    this.container.appendChild(root);
    return root;
  }

  private createScreenContainer(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'sdl-screen-container';
    this.root.appendChild(container);
    return container;
  }

  private createNavigation(): HTMLElement {
    const nav = document.createElement('nav');
    nav.className = 'sdl-nav';

    NAV_ITEMS.forEach((item) => {
      const button = document.createElement('button');
      button.className = `sdl-nav-item${item.id === this.currentScreen ? ' active' : ''}${item.hasBadge ? ' has-badge' : ''}`;
      button.dataset.screen = item.id;
      button.innerHTML = `
        <img class="sdl-nav-icon" src="${item.icon}" alt="${item.label}" />
        <span class="sdl-nav-label">${item.label}</span>
      `;
      button.addEventListener('click', () => this.navigateTo(item.id));
      nav.appendChild(button);
    });

    this.root.appendChild(nav);
    return nav;
  }

  private createModal(): {
    overlay: HTMLElement;
    container: HTMLElement;
    content: HTMLElement;
    icon: HTMLElement;
    title: HTMLElement;
  } {
    const overlay = document.createElement('div');
    overlay.className = 'sdl-modal-overlay';
    overlay.addEventListener('click', () => this.closeModal());

    const container = document.createElement('div');
    container.className = 'sdl-modal-container';

    const header = document.createElement('div');
    header.className = 'sdl-modal-header';

    const headerLeft = document.createElement('div');
    headerLeft.className = 'sdl-modal-header-left';

    const icon = document.createElement('span');
    icon.className = 'sdl-modal-header-icon';

    const title = document.createElement('span');
    title.className = 'sdl-modal-header-title';

    headerLeft.appendChild(icon);
    headerLeft.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'sdl-modal-close';
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', () => this.closeModal());

    header.appendChild(headerLeft);
    header.appendChild(closeBtn);

    const content = document.createElement('div');
    content.className = 'sdl-modal-content';

    container.appendChild(header);
    container.appendChild(content);

    this.root.appendChild(overlay);
    this.root.appendChild(container);

    return { overlay, container, content, icon, title };
  }

  private setupTouchSwipe(): void {
    let touchStartX = 0;
    let touchEndX = 0;

    this.screenContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.screenContainer.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  private handleSwipe(startX: number, endX: number): void {
    // Skip screen navigation while a dice-loadout drag is in progress.
    // The DiceScreen sets this body dataset for the duration of a drag
    // because a diagonal drag from a grid thumb to a loadout slot can
    // cross the 80px horizontal threshold and be misread as a screen
    // swipe — drops in the center slot would otherwise navigate away.
    if (document.body.dataset.diceDragging === 'true') return;

    const diff = startX - endX;
    const threshold = 80;

    if (Math.abs(diff) < threshold) return;

    // Swipe gestures only navigate between bottom-nav screens. Sub-screens
    // (like 'lobby') are excluded so users do not stumble into them via swipe.
    const currentIndex = SWIPEABLE_SCREENS.indexOf(this.currentScreen);
    if (currentIndex === -1) return;

    if (diff > 0 && currentIndex < SWIPEABLE_SCREENS.length - 1) {
      this.navigateTo(SWIPEABLE_SCREENS[currentIndex + 1]);
    } else if (diff < 0 && currentIndex > 0) {
      this.navigateTo(SWIPEABLE_SCREENS[currentIndex - 1]);
    }
  }

  /**
   * Register a screen with the UI manager.
   *
   * Uses classList.add (NOT className =) so that classes set by the screen
   * itself (e.g. StoreScreen adds 'store-screen' for scoped CSS) are preserved.
   */
  registerScreen(id: ScreenId, element: HTMLElement): void {
    element.classList.add('sdl-screen');
    if (id === this.currentScreen) element.classList.add('active');
    element.dataset.screen = id;
    this.screens.set(id, element);
    this.screenContainer.appendChild(element);
  }

  /**
   * Navigate to a screen with slide animation
   */
  navigateTo(targetScreen: ScreenId): void {
    if (targetScreen === this.currentScreen || this.isAnimating) return;

    const currentEl = this.screens.get(this.currentScreen);
    const targetEl = this.screens.get(targetScreen);

    if (!currentEl || !targetEl) {
      console.warn(`Screen not found: ${targetScreen}`);
      return;
    }

    this.isAnimating = true;

    const currentIndex = SCREENS_ORDER.indexOf(this.currentScreen);
    const targetIndex = SCREENS_ORDER.indexOf(targetScreen);
    const goingLeft = targetIndex > currentIndex;

    // Step 1: Position target screen off-screen
    targetEl.classList.remove('active', 'sliding', 'off-left', 'off-right');
    targetEl.classList.add(goingLeft ? 'off-right' : 'off-left');

    // Force reflow
    targetEl.offsetHeight;

    // Step 2: Add transition class to both screens
    currentEl.classList.add('sliding');
    targetEl.classList.add('sliding');

    // Step 3: Animate both screens together
    requestAnimationFrame(() => {
      currentEl.classList.remove('active');
      currentEl.classList.add(goingLeft ? 'off-left' : 'off-right');

      targetEl.classList.remove('off-left', 'off-right');
      targetEl.classList.add('active');
    });

    // Update nav highlight + visibility (slides in lockstep with screens)
    this.updateNavActive(targetScreen);
    this.setNavHidden(HIDES_NAV.has(targetScreen));

    // Emit event
    this.emit('screen:change', { from: this.currentScreen, to: targetScreen });

    // Clean up after animation
    setTimeout(() => {
      currentEl.classList.remove('sliding', 'off-left', 'off-right');
      targetEl.classList.remove('sliding');
      this.currentScreen = targetScreen;
      this.isAnimating = false;
    }, 350);
  }

  private updateNavActive(screenId: ScreenId): void {
    const navItems = this.nav.querySelectorAll('.sdl-nav-item');
    navItems.forEach((item) => {
      const el = item as HTMLElement;
      el.classList.toggle('active', el.dataset.screen === screenId);
    });
  }

  private setNavHidden(hidden: boolean): void {
    this.nav.classList.toggle('sdl-nav-hidden', hidden);
  }

  /**
   * Open a screen as a fullscreen modal
   */
  openModal(screenId: ScreenId, content?: HTMLElement): void {
    const config = MODAL_CONFIG[screenId];
    if (!config) return;

    this.modalIcon.textContent = config.icon;
    this.modalTitle.textContent = config.title;

    if (content) {
      this.modalContent.innerHTML = '';
      this.modalContent.appendChild(content);
    } else {
      // Use content from registered screen
      const screen = this.screens.get(screenId);
      if (screen) {
        const screenContent = screen.querySelector('.sdl-screen-content, .sdl-empty-state');
        if (screenContent) {
          this.modalContent.innerHTML = '';
          this.modalContent.appendChild(screenContent.cloneNode(true));
        }
      }
    }

    this.modalOverlay.classList.add('active');
    this.modalContainer.classList.add('active');
    this.nav.style.display = 'none';
    this.isModalOpen = true;

    this.emit('modal:open', { screenId });
  }

  /**
   * Close the modal
   */
  closeModal(): void {
    this.modalOverlay.classList.remove('active');
    this.modalContainer.classList.remove('active');
    this.nav.style.display = 'flex';
    this.isModalOpen = false;

    this.emit('modal:close', {});
  }

  /**
   * Update nav badge visibility
   */
  setNavBadge(screenId: ScreenId, visible: boolean): void {
    const navItem = this.nav.querySelector(`[data-screen="${screenId}"]`);
    if (navItem) {
      navItem.classList.toggle('has-badge', visible);
    }
  }

  /**
   * Get current screen
   */
  getCurrentScreen(): ScreenId {
    return this.currentScreen;
  }

  /**
   * Check if modal is open
   */
  isModal(): boolean {
    return this.isModalOpen;
  }

  /**
   * Show the UI (after loading)
   */
  show(): void {
    this.root.classList.add('visible');
  }

  /**
   * Hide the UI
   */
  hide(): void {
    this.root.classList.remove('visible');
  }

  /**
   * Destroy the UI
   */
  destroy(): void {
    this.root.remove();
    this.screens.clear();
    this.removeAllListeners();
  }
}
